import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * A vertical rail-and-node timeline. Two callers in the app draw a timeline and their CSS families
 * are NOT visually identical, so this primitive keeps both as distinct `variant`s — each renders its
 * EXACT original class set for zero visual change:
 *
 *  - `variant="deadline"` — the proposal deadline timeline (`.deadline-timeline` wrapping `.dt-item`
 *    rows; rail `.dt-rail` > `.dt-node`/`.dt-line`; content `.dt-content` with an `<h4>` title and a
 *    `.dt-sub`). Nodes carry a state class (`done`/`active`/`overdue`; pending = none) and a done line
 *    is tinted green via `.dt-line.done`. Numerals/checks come from `node`.
 *  - `variant="audit"` — the per-trip audit timeline (TW-3457). Rows are bare `.audit-row` siblings
 *    (no wrapper); rail `.audit-rail` > `.audit-node`/`.audit-line`; content `.audit-content` with a
 *    `<b>` title and a `.au-user` sub; an optional trailing `.audit-time` from `meta`. Audit nodes
 *    have no state styling in the original, so `state` is ignored for this variant.
 *
 * In both originals the trailing connector line is suppressed on the last item — Timeline does the
 * same. All visible text arrives via props (i18n stays with the caller).
 */
export type TimelineVariant = "deadline" | "audit";

/** Visual state of a node. Only the `deadline` variant styles nodes by state. */
export type TimelineItemState = "done" | "active" | "overdue" | "pending";

export interface TimelineItem {
  /** Stable React key for the row. */
  key: string;
  /** Node glyph — a numeral, a check `<Icon>`, or any icon element. */
  node: ReactNode;
  /** Primary line of the row (rendered as `<h4>` for `deadline`, `<b>` for `audit`). */
  title: ReactNode;
  /** Secondary line (`.dt-sub` for `deadline`, `.au-user` for `audit`). Omit to hide. */
  sub?: ReactNode;
  /**
   * Trailing meta slot. For `deadline` it sits inside the content row (e.g. the due/overdue chip);
   * for `audit` it renders the trailing `.audit-time`. Omit to render nothing.
   */
  meta?: ReactNode;
  /** Node/line state. `deadline` only: `done` (green), `active`, `overdue`; `pending` = unstyled. */
  state?: TimelineItemState;
}

export interface TimelineProps {
  variant: TimelineVariant;
  items: TimelineItem[];
  /** Forwarded to the `deadline` wrapper (the audit variant has no wrapper). */
  style?: CSSProperties;
  /** Extra class on the `deadline` wrapper. */
  className?: string;
}

export function Timeline({ variant, items, style, className }: TimelineProps) {
  if (variant === "audit") {
    // No wrapper in the original — rows are direct siblings.
    return (
      <>
        {items.map((item, i) => (
          <div className="audit-row" key={item.key}>
            <div className="audit-rail">
              <span className="audit-node">{item.node}</span>
              {i < items.length - 1 && <span className="audit-line" />}
            </div>
            <div className="audit-content">
              <b>{item.title}</b>
              {item.sub !== undefined && <div className="au-user">{item.sub}</div>}
            </div>
            {item.meta !== undefined && <span className="audit-time">{item.meta}</span>}
          </div>
        ))}
      </>
    );
  }

  // variant === "deadline"
  return (
    <div className={cn("deadline-timeline", className)} style={style}>
      {items.map((item, i) => {
        const done = item.state === "done";
        const nodeState = item.state && item.state !== "pending" ? item.state : "";
        return (
          <div className="dt-item" key={item.key}>
            <div className="dt-rail">
              <span className={cn("dt-node", nodeState)}>{item.node}</span>
              {i < items.length - 1 && <span className={cn("dt-line", done && "done")} />}
            </div>
            <div className="dt-content">
              <div className="row-between" style={{ alignItems: "flex-start" }}>
                <div>
                  <h4>{item.title}</h4>
                  {item.sub !== undefined && <div className="dt-sub">{item.sub}</div>}
                </div>
                {item.meta}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
